import {gql} from '@apollo/client';
import assert from 'assert';
import React from 'react';

import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import getTestProps from 'test/lib/cypress/getTestProps';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import APIUtil from 'util/APIUtil';

import {
  TestOrdersQuery_folderItem_BasicFolderItemNode_studies_StudyNode as StudyItemType,
  TestOrdersQuery,
  TestOrdersQueryVariables,
} from './__generated/TestOrdersQuery';
import {TestOrdersViewTable} from './TestOrdersViewTable';

export const TEST_ORDERS_QUERY = gql`
  query TestOrdersQuery($fileId: StrictString!) @skipAudit @engComponent(name: SAMPLE_TESTS) {
    folderItem(id: $fileId) {
      ... on RegistryItemInterface {
        id
        ...StudyableTypedValueDataGraphQL
        testOrders {
          id
          name
          status
          analysisComment
          dilutionFactor
          container {
            ... on ContainerNode {
              id
              name
            }
            ... on InaccessibleInfoNode {
              ...InaccessibleInfoGraphQL
            }
          }
          testDefinitionCorrect {
            ... on TestDefinitionNode {
              id
              name
            }
            ... on InaccessibleInfoNode {
              ...InaccessibleInfoGraphQL
            }
          }
          procedure {
            ... on ProcedureNode {
              id
              name
            }
            ... on InaccessibleInfoNode {
              ...InaccessibleInfoGraphQL
            }
          }
          result {
            ... on ResultNode {
              id
              tagsJson
              schema {
                ... on ResultSchemaNode {
                  id
                  name
                  fieldsJson
                }
              }
            }
            ... on InaccessibleInfoNode {
              ...InaccessibleInfoGraphQL
            }
          }
          procedureMethodExecutionInstance {
            ... on ProcedureMethodExecutionInstanceNode {
              id
              worksheet {
                ... on WorksheetNode {
                  id
                  name
                }
                ...InaccessibleInfoGraphQL
              }
            }
            ...InaccessibleInfoGraphQL
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${getFragmentWrapper('StudyableTypedValueData').fragment}
`;

interface Props {
  modelId: string;
}

export default function TestOrdersView(props: Props): JSX.Element {
  // TODO(BNCH-105724): add support for containers
  assert(APIUtil.isBiologicalEntity(props.modelId), 'Expected an entity ID');
  return (
    <div className="react-tab-container" {...getTestProps({component: 'TestOrdersView', element: 'root'})}>
      <BenchlingQuery<TestOrdersQuery, TestOrdersQueryVariables>
        query={TEST_ORDERS_QUERY}
        variables={{fileId: props.modelId}}
        renderLoading={() => <CenteredHorizontalThrobber />}
      >
        {(data) => {
          if (isInaccessible(data.folderItem)) {
            // If this is a read-only share link, the full folder item will be inaccessible and we don't want
            // to render a test orders table at all
            return null;
          }

          // eslint-disable-next-line benchlint/no-in-operator
          if (!('testOrders' in data.folderItem)) {
            // Folder items that don't implement RegistryItemInterface don't have test orders, we would not render a tab for them.
            return null;
          }

          // eslint-disable-next-line benchlint/no-in-operator
          if (!('studies' in data.folderItem)) {
            // Folder items that aren't studyable don't have test orders, we would not render a tab for them.
            return null;
          }

          const studyIds = data.folderItem.studies
            .filter((study) => !isInaccessible(study))
            .map((study) => (study as StudyItemType).id);

          return (
            <TestOrdersViewTable
              testOrders={data.folderItem.testOrders}
              itemId={props.modelId}
              studyIds={studyIds}
            />
          );
        }}
      </BenchlingQuery>
    </div>
  );
}
